import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Wand2, Calculator, Loader2, ArrowLeftRight } from "lucide-react";
import { API } from "@/lib/data";

export const AdminVisualizerTab = ({ token }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pkgId, setPkgId] = useState("estate");
  const [busy, setBusy] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [history, setHistory] = useState([]);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/visualizations`, { headers });
      setHistory(data.visualizations);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setPackages(r.data.packages || [])).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const fetchBlob = async (path) => {
    const res = await axios.get(`${API}/admin/files/${path}`, { headers, responseType: "blob" });
    return URL.createObjectURL(res.data);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResultUrl(null);
    setEstimate(null);
    e.target.value = "";
  };

  const generate = async () => {
    if (!file) {
      toast.error("Take or upload a photo of the home first");
      return;
    }
    setBusy(true);
    setResultUrl(null);
    setEstimate(null);
    try {
      const fd = new FormData();
      fd.append("package_id", pkgId);
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/visualize`, fd, { headers, timeout: 180000 });
      const url = await fetchBlob(data.visualization.storage_path);
      setResultUrl(url);
      toast.success("Mockup ready!");
      const fd2 = new FormData();
      fd2.append("package_id", pkgId);
      fd2.append("file", file);
      const est = await axios.post(`${API}/admin/estimate`, fd2, { headers, timeout: 120000 });
      setEstimate(est.data.estimate);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const viewHistory = async (v) => {
    try {
      const url = await fetchBlob(v.storage_path);
      setResultUrl(url);
      setEstimate(null);
    } catch {
      toast.error("Could not load that mockup");
    }
  };

  return (
    <div data-testid="admin-visualizer-tab">
      <h2 className="font-display text-2xl font-bold text-amber-50">Mockup Studio</h2>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-emerald-100/50">
        Snap a photo of a customer's home, pick a package, and see the house lit up so they know exactly what they're buying — plus an automatic materials list and upsell ideas.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl border-2 border-dashed border-emerald-500/30">
            {preview ? (
              <img src={preview} alt="Customer home" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
                <Camera className="text-amber-400" size={28} />
                <span className="text-sm text-emerald-100/60">No photo yet — take one on-site or upload from your gallery</span>
              </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              onClick={() => cameraRef.current?.click()}
              data-testid="visualizer-camera-button"
              className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 py-3 text-xs font-bold uppercase tracking-widest text-stone-950 gold-glow"
            >
              <Camera size={15} /> Take Photo
            </button>
            <button
              onClick={() => uploadRef.current?.click()}
              data-testid="visualizer-upload-button"
              className="flex items-center justify-center gap-2 rounded-full border border-amber-500/40 py-3 text-xs font-bold uppercase tracking-widest text-amber-300 transition-all hover:bg-amber-500/10"
            >
              <Upload size={15} /> Upload Photo
            </button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} data-testid="visualizer-camera-input" className="hidden" />
          <input ref={uploadRef} type="file" accept="image/*" onChange={onFile} data-testid="visualizer-file-input" className="hidden" />

          <p className="mb-2 mt-5 font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/50">Package to preview</p>
          <div className="grid grid-cols-3 gap-2">
            {packages.map((p) => (
              <button
                key={p.id}
                onClick={() => setPkgId(p.id)}
                data-testid={`visualizer-package-${p.id}`}
                className={`rounded-xl border px-3 py-2.5 text-left transition-all ${pkgId === p.id ? "border-amber-400 bg-amber-500/10" : "border-emerald-500/25 hover:border-amber-500/40"}`}
              >
                <span className="font-display block text-base font-bold text-amber-50">${p.price}</span>
                <span className="block text-[10px] leading-tight text-emerald-100/60">{p.name}</span>
              </button>
            ))}
          </div>

          <button
            onClick={generate}
            disabled={busy}
            data-testid="visualizer-generate-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 py-4 text-sm font-bold uppercase tracking-widest text-stone-950 gold-glow disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {busy ? "Lighting the house…" : "Light It Up"}
          </button>
          <p className="mt-2 text-center font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/40">
            Takes ~30 seconds · also builds your shopping list
          </p>
        </div>

        <div className="rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
          <p className="mb-3 flex items-center gap-2 font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/50">
            <ArrowLeftRight size={12} className="text-amber-400" /> Before / After
          </p>
          <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl bg-[#06130D]/70">
            {busy ? (
              <div className="flex flex-col items-center gap-3 text-amber-300">
                <Loader2 size={32} className="animate-spin" />
                <span className="font-mono-x text-[10px] uppercase tracking-[0.25em]">Hanging lights…</span>
              </div>
            ) : resultUrl ? (
              <img src={resultUrl} alt="Home mockup with lights" data-testid="visualizer-result-image" className="h-full w-full object-cover" />
            ) : (
              <span className="px-8 text-center text-sm text-emerald-100/40">Your finished mockup will appear here</span>
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/50">Recent mockups</p>
              <div className="flex flex-wrap gap-2">
                {history.slice(0, 6).map((v) => (
                  <button
                    key={v.id}
                    onClick={() => viewHistory(v)}
                    className="rounded-full border border-emerald-500/25 px-3 py-1.5 font-mono-x text-[10px] uppercase tracking-wider text-emerald-100/60 transition-colors hover:border-amber-400/60 hover:text-amber-300"
                  >
                    {v.package_id} · {new Date(v.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {estimate && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="estimate-panel">
          <div className="rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
            <p className="flex items-center gap-2 font-mono-x text-[11px] uppercase tracking-[0.22em] text-amber-400">
              <Calculator size={14} /> Shopping List
            </p>
            <table className="mt-4 w-full text-left text-sm" data-testid="estimate-materials-table">
              <thead className="font-mono-x text-[10px] uppercase tracking-[0.15em] text-emerald-100/50">
                <tr><th className="pb-2">Item</th><th className="pb-2">Qty</th><th className="pb-2 text-right">Est. Cost</th></tr>
              </thead>
              <tbody className="divide-y divide-amber-500/10">
                {(estimate.materials || []).map((m, i) => (
                  <tr key={i}>
                    <td className="py-2.5 pr-2 text-emerald-100/85">{m.item}</td>
                    <td className="py-2.5 pr-2 text-emerald-100/60">{m.quantity}</td>
                    <td className="py-2.5 text-right text-amber-300">{m.est_cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {estimate.labor_hours && (
              <p className="mt-3 border-t border-amber-500/10 pt-3 text-xs text-emerald-100/60">Estimated labor: {estimate.labor_hours}</p>
            )}
            {estimate.notes && (
              <p className="mt-2 text-xs italic leading-relaxed text-emerald-100/50">{estimate.notes}</p>
            )}
          </div>
          <div className="rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
            <p className="font-mono-x text-[11px] uppercase tracking-[0.22em] text-amber-400">Upsell Ideas for This Home</p>
            <div className="mt-4 space-y-3" data-testid="estimate-upsells">
              {(estimate.upsells || []).map((u, i) => (
                <div key={i} className="rounded-xl border border-emerald-500/20 bg-[#06130D]/60 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-bold text-amber-50">{u.title}</span>
                    <span className="font-mono-x text-xs text-amber-300">{u.price}</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-emerald-100/60">{u.pitch}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
